export const PRICING_PUBLIC_PAGE_TEXT = {
    en: {
        seo: {
            title: "Virtalibry Pricing – Plans for KDP Tools, Publishing Workflows, and Author Growth",
            description:
                "Compare Virtalibry plans for authors and publishers. Explore free and paid options for KDP tools, publishing workflows, account limits, and broader author growth features.",
            keywords:
                "Virtalibry pricing, Virtalibry plans, kdp tools pricing, author tools pricing, publishing workflow plans, Virtalibry subscription, kdp platform plans, author growth platform pricing",
            image: "https://virtalibry.com/face/why_love_is_gold_new.png",
            type: "website",

            webPage: {
                name: "Virtalibry Pricing",
                url: "https://virtalibry.com/pricing",
                description:
                    "Official pricing page for Virtalibry. Compare plans, explore free and paid options, and understand how the platform supports KDP tools, workflows, and author growth.",
                datePublished: "2025-11-01T00:00:00Z",
                dateModified: "2025-12-19T00:00:00Z",
            },

            organization: {
                name: "Virtalibry",
                url: "https://virtalibry.com",
                logo: "https://virtalibry.com/logo/virtalibry_dark_free_no_bg.png",
                sameAs: [
                    "https://www.facebook.com/virtalibry",
                    "https://www.youtube.com/@virtalibry"
                ],
                contactPoint: [
                    {
                        "@type": "ContactPoint",
                        contactType: "billing support",
                        email: "info@virtalibry.com",
                        availableLanguage: ["English"]
                    }
                ]
            },

            webApp: {
                type: "WebApplication",
                name: "Virtalibry KDP Tools & Growth Platform",
                url: "https://virtalibry.com",
                applicationCategory: "BusinessApplication",
                operatingSystem: "All",
                offers: {
                    price: "0",
                    priceCurrency: "USD",
                    url: "https://virtalibry.com/pricing"
                }
            },

            additionalSchemas: [
                {
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "@id": "https://virtalibry.com/pricing",
                    name: "Virtalibry Pricing",
                    url: "https://virtalibry.com/pricing",
                    isPartOf: {
                        "@type": "WebSite",
                        name: "Virtalibry",
                        url: "https://virtalibry.com"
                    }
                },
                {
                    "@context": "https://schema.org",
                    "@type": "Service",
                    name: "Virtalibry",
                    url: "https://virtalibry.com",
                    description:
                        "A platform for authors and publishers with KDP tools, publishing workflows, account features, and broader growth support.",
                    provider: {
                        "@type": "Organization",
                        name: "Virtalibry",
                        url: "https://virtalibry.com",
                        logo: {
                            "@type": "ImageObject",
                            url: "https://virtalibry.com/face/why_love_is_gold_new.png"
                        }
                    }
                },
                {
                    "@context": "https://schema.org",
                    "@type": "OfferCatalog",
                    name: "Virtalibry Plans",
                    url: "https://virtalibry.com/pricing",
                    itemListElement: [
                        {
                            "@type": "Offer",
                            name: "Free Plan",
                            url: "https://virtalibry.com/pricing",
                            price: "0",
                            priceCurrency: "USD",
                            availability: "https://schema.org/InStock"
                        },
                        {
                            "@type": "Offer",
                            name: "Premium Plans",
                            url: "https://virtalibry.com/pricing",
                            availability: "https://schema.org/InStock"
                        }
                    ]
                }
            ],

            faq: [
                {
                    q: "Is there a free plan on Virtalibry?",
                    a: "Yes. Virtalibry includes a free entry point so authors can explore the platform and understand how the workflow is structured before upgrading."
                },
                {
                    q: "What do paid plans unlock?",
                    a: "Paid plans unlock broader limits, additional account capacity, and expanded platform features for authors who need more room to work and grow."
                },
                {
                    q: "Can I change or cancel my plan later?",
                    a: "Yes. You can upgrade, downgrade, or cancel your subscription from your account settings."
                },
                {
                    q: "Who should use the pricing page?",
                    a: "The pricing page is designed for authors, publishers, and users comparing the free and paid ways to use Virtalibry as part of a broader KDP workflow."
                },
                {
                    q: "Does pricing depend on the kind of author workflow I need?",
                    a: "Yes. Different users choose different plans depending on their publishing goals, account scale, and how deeply they want to use the platform."
                }
            ],

            breadcrumb: {
                homeName: "Virtalibry",
                pageName: "Pricing"
            }
        },

        hero: {
            h1: "Virtalibry Pricing: Plans for KDP Tools, Publishing Workflows, and Author Growth",
            p1:
                "Use this page to compare Virtalibry plans and understand how the platform supports different levels of author activity, publishing workflows, and broader KDP growth needs.",
            p2:
                "Instead of presenting pricing as a simple upgrade table, this page positions each plan as part of a larger publishing system that can grow with your goals, tools, and workflow complexity.",
            p3:
                "Whether you are exploring the free version or deciding when it makes sense to expand into a larger plan, this page helps clarify what each level is meant to support.",

            bullets: [
                "Compare free and paid Virtalibry plans",
                "Understand how pricing connects to broader publishing workflows",
                "Choose the level that matches your author goals and platform usage",
                "Use this page as the canonical public pricing overview"
            ]
        },

        blocks: {
            block1_H2: "A Clear Pricing Page for the Virtalibry Ecosystem",
            block1_P:
                "This page works best as the main public pricing overview for Virtalibry. It helps authors and publishers understand the difference between free and paid access while keeping pricing connected to the broader publishing workflow.",

            block2_H3: "Main Plan Comparison Lives in the Tool Block",
            block2_P:
                "The actual plan cards, billing controls, and upgrade options can live inside the pricing component below, while the page template keeps the layout visually consistent with the rest of the site.",

            block3_H3: "How It Fits the Brand and Tools Cluster",
            block3_P:
                "The pricing page supports the wider Virtalibry ecosystem by helping users understand how account limits, platform access, and broader growth features fit together inside one structured publishing environment.",

            block4_H3: "Designed for SEO and User Reassurance",
            block4_P:
                "The template version keeps the page aligned with the rest of the public site, preserves structured SEO data, and still lets the actual pricing experience live inside the core component where users expect it.",

            block5_H3: "Use It as the Canonical Public Pricing Page",
            block5_P:
                "As Virtalibry expands, this page should remain the canonical pricing destination for the platform, while other pages can link into it from tools, workflow guides, onboarding, and support sections."
        }
    },
};

export const pickPricingPublicPageT = (lang) =>
    PRICING_PUBLIC_PAGE_TEXT?.[lang] || PRICING_PUBLIC_PAGE_TEXT.en;